/**
 * J2 upload-parity: destination labels, UI-identical authz gates, and receipt
 * hrefs for file-taking propose-delivery-action kinds. Keep in lockstep with
 * services/shanks/pkg/tools/delivery_action_destinations.go.
 */ import { pathFor, programDocs } from '@mono/app-routes';
export const AUTH_RECORD_DOCUMENTS = 'recordDocuments';
export const AUTH_CONTRACT_DOCUMENTS = 'contractDocuments';
export const AUTH_EXECUTION = 'execution';
export const AUTH_QUALITY_SCORES = 'qualityScores';
export const AUTH_QUALITY = 'quality';
export const AUTH_TOUCHPOINTS = 'touchpoints';
export const AUTH_PROGRAM_UPDATE = 'programUpdate';
export const AUTH_PROJECT_WRITE = 'projectWrite';
export const AUTH_TENANT = 'tenant';
export const AUTH_CAPTURE_WRITE = 'captureWrite';
export const AUTH_RFI_WRITE = 'rfiWrite';
export const deliveryFileMetaByAction = {
    create_program_document: {
        surface: 'Docs',
        authzGate: AUTH_RECORD_DOCUMENTS,
        needsProgram: true,
        consequence: 'The file is recorded on Docs. Contract / attachment / SOW categories start a read in the processing tray.',
        whoCan: 'Someone with documents create on this program (a program lead, not an External Auditor).'
    },
    upload_modification: {
        surface: 'Contract changes',
        authzGate: AUTH_CONTRACT_DOCUMENTS,
        needsProgram: true,
        consequence: 'The PDF is queued for modification review. It is not committed.',
        whoCan: 'Someone who can upload contract documents on this program.'
    },
    create_submission: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file is filed as submission evidence on that date.',
        whoCan: 'Someone with execution write on this program.'
    },
    propose_submission_evidence: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file is proposed as added evidence (two-person).',
        whoCan: 'Someone with execution write on this program.'
    },
    propose_waiver_evidence: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file is proposed as evidence on the approved waiver (two-person). The waiver itself does not change.',
        whoCan: 'Someone with execution write on this program.'
    },
    propose_waiver_edit: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The waiver changes only once a second person approves. If the waiver changes again first, approval is refused and the edit has to be proposed again.',
        whoCan: 'Someone with execution write on this program.'
    },
    propose_submission_edit: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file replaces or joins the submission attachments (two-person).',
        whoCan: 'Someone with execution write on this program.'
    },
    record_cor_answer: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file is recorded as COR-answer evidence.',
        whoCan: 'Someone with execution write on this program.'
    },
    log_contract_event: {
        surface: 'Deliverables',
        authzGate: AUTH_EXECUTION,
        needsProgram: true,
        consequence: 'The file is stored as event evidence (minutes / attendee list).',
        whoCan: 'Someone with execution write on this program.'
    },
    create_work_product: {
        surface: 'Work products',
        authzGate: AUTH_PROGRAM_UPDATE,
        needsProgram: true,
        consequence: 'The file is attached to the work product.',
        whoCan: 'Someone who can edit this program.'
    },
    update_work_product: {
        surface: 'Work products',
        authzGate: AUTH_PROGRAM_UPDATE,
        needsProgram: true,
        consequence: 'The file replaces the work product attachments.',
        whoCan: 'Someone who can edit this program.'
    },
    extract_cpars_score: {
        surface: 'Quality',
        authzGate: AUTH_QUALITY_SCORES,
        needsProgram: true,
        consequence: 'Specra reads the PDF. Nothing is saved until a second card writes the score.',
        whoCan: 'Someone who can record quality scores on this program.'
    },
    upsert_cpars_score: {
        surface: 'Quality',
        authzGate: AUTH_QUALITY_SCORES,
        needsProgram: true,
        consequence: 'The PDF is bound as the CPARS source and the score is saved.',
        whoCan: 'Someone who can record quality scores on this program.'
    },
    extract_qasp_score: {
        surface: 'Quality',
        authzGate: AUTH_QUALITY_SCORES,
        needsProgram: true,
        consequence: 'Specra reads the PDF. Nothing is saved until a second card writes the score.',
        whoCan: 'Someone who can record quality scores on this program.'
    },
    upsert_qasp_score: {
        surface: 'Quality',
        authzGate: AUTH_QUALITY_SCORES,
        needsProgram: true,
        consequence: 'The PDF is bound as the QASP source and the score is saved.',
        whoCan: 'Someone who can record quality scores on this program.'
    },
    upsert_deviation: {
        surface: 'Approved Changes',
        authzGate: AUTH_QUALITY,
        needsProgram: true,
        consequence: 'The file is linked to the deviation. Approval-evidence processing starts when asked.',
        whoCan: 'Someone who can edit Approved Changes on this program.'
    },
    upsert_customer_touchpoint: {
        surface: 'Touchpoints',
        authzGate: AUTH_TOUCHPOINTS,
        needsProgram: true,
        consequence: 'The file is attached to the touchpoint. Notes extraction starts when asked.',
        whoCan: 'Someone with growth write on this program.'
    },
    upload_project_files: {
        surface: 'Files',
        authzGate: AUTH_PROJECT_WRITE,
        needsProgram: false,
        consequence: 'The file is attached to the project and processing starts.',
        whoCan: 'A project owner or member.'
    },
    upload_award_intake: {
        surface: 'Award intake',
        authzGate: AUTH_PROJECT_WRITE,
        needsProgram: false,
        consequence: 'The award document is processed. Begin comparison stays a separate step unless asked.',
        whoCan: 'A pre-award project owner or member.'
    },
    extract_pricing_sheet: {
        surface: 'Registries',
        authzGate: AUTH_TENANT,
        needsProgram: false,
        consequence: 'Specra reads the sheet and stages rate observations for review. Nothing enters the rate book until you commit them.',
        whoCan: 'Anyone in the tenant (the UI has no tighter gate).'
    },
    file_company_document: {
        surface: 'Registries',
        authzGate: AUTH_TENANT,
        needsProgram: false,
        consequence: 'The file is filed on the company in Registries › Companies, under Documents.',
        whoCan: 'Someone with registries authority on the organization (e.g. growth_ops or delivery_vp).'
    },
    link_capture_document: {
        surface: 'Documents',
        authzGate: AUTH_CAPTURE_WRITE,
        needsProgram: false,
        consequence: 'The file is linked to that capture record.',
        whoCan: 'Someone who can write that capture record.'
    },
    attach_rfi_package: {
        surface: 'RFI package',
        authzGate: AUTH_RFI_WRITE,
        needsProgram: false,
        consequence: 'The file is attached as the externally authored final for that package.',
        whoCan: 'Someone who can update that RFI response.'
    },
    save_external_capture_output: {
        surface: 'Outputs',
        authzGate: AUTH_PROJECT_WRITE,
        needsProgram: false,
        consequence: 'The file is saved as a new externally authored output version.',
        whoCan: 'A capture project owner or member.'
    },
    // Pursuit writes (delivery_action_registry_capture.go captureMeta).
    log_capture_interaction: pursuitMeta('Interactions', "It is added to the pursuit's Interactions."),
    record_capture_intel: pursuitMeta('Competitive field', "It is recorded as evidence in the pursuit's competitive field, with its source. Evidence is append-only."),
    link_capture_competitor: pursuitMeta('Competitors', 'The company is listed as a competitor on the pursuit.'),
    link_capture_teaming_partner: pursuitMeta('Teaming', 'The company is listed as a teaming partner on the pursuit.'),
    upsert_capture_task: pursuitMeta('Action plan', "The task is saved on the pursuit's action plan."),
    update_capture_profile: pursuitMeta('Capture profile', 'Only the fields shown change; the rest of the profile stays.'),
    log_rfi_event: pursuitMeta('RFIs', "The event is added to the pursuit's RFIs.")
};
function pursuitMeta(surface, consequence) {
    return {
        surface,
        authzGate: AUTH_CAPTURE_WRITE,
        needsProgram: false,
        consequence,
        whoCan: 'Someone who can edit this pursuit (its capture manager or a pursuit member with edit access).'
    };
}
export function deliveryActionNeedsProgram(action) {
    if (action === 'skip' || action === 'identity' || action === '') {
        return false;
    }
    const meta = deliveryFileMetaByAction[action];
    return meta ? meta.needsProgram : true;
}
export function destinationBreadcrumb(...parts) {
    return parts.map((p)=>(p ?? '').trim()).filter(Boolean).join(' › ');
}
export function normalizeProgramDocumentCategory(cat) {
    switch(cat.trim().toLowerCase()){
        case 'sow':
        case 'sow / execution plan':
        case 'sow/execution plan':
            return 'SOW / Execution Plan';
        case 'attachment':
        case 'attachment/addendum':
        case 'attachment / addendum':
            return 'Attachment / Addendum';
        default:
            return cat.trim();
    }
}
export function isSowDocumentCategory(cat) {
    return normalizeProgramDocumentCategory(cat) === 'SOW / Execution Plan';
}
export function isAttachmentDocumentCategory(cat) {
    const n = normalizeProgramDocumentCategory(cat);
    return n === 'Attachment / Addendum' || cat === 'Attachment';
}
export function isContractDocumentCategory(cat) {
    return normalizeProgramDocumentCategory(cat) === 'Contract';
}
export function verdictForGate(gate, access) {
    const metaWho = Object.values(deliveryFileMetaByAction).find((m)=>m.authzGate === gate)?.whoCan;
    const deny = (permitted)=>permitted ? {
            permitted: true
        } : {
            permitted: false,
            reason: 'Not permitted',
            whoCan: metaWho
        };
    switch(gate){
        case AUTH_RECORD_DOCUMENTS:
            return deny(access.recordDocuments);
        case AUTH_CONTRACT_DOCUMENTS:
            return deny(access.contractDocuments);
        case AUTH_EXECUTION:
            return deny(access.execution);
        case AUTH_QUALITY_SCORES:
            return deny(access.qualityScores);
        case AUTH_QUALITY:
            return deny(access.quality);
        case AUTH_TOUCHPOINTS:
            return deny(access.touchpoints);
        case AUTH_PROGRAM_UPDATE:
            return deny(access.comment);
        case AUTH_PROJECT_WRITE:
        case AUTH_RFI_WRITE:
            return deny(access.projectCanEdit);
        case AUTH_TENANT:
        case AUTH_CAPTURE_WRITE:
            return deny(access.signedIn);
        default:
            return {
                permitted: true
            };
    }
}
// Receipt links come from the route manifest (@mono/app-routes), so a link
// can only point at a page the app actually has — a renamed or removed route
// fails the build instead of shipping a dead receipt.
export function receiptHrefForAction(action, ctx) {
    const programId = ctx.programId ?? '';
    const program = (id)=>programId ? pathFor(id, {
            programId
        }) : '';
    switch(action){
        case 'create_program_document':
            if (programId && isSowDocumentCategory(ctx.category ?? '')) {
                return programDocs(programId, {
                    tab: 'modifications'
                });
            }
            if (programId && ctx.docId) return programDocs(programId, {
                doc: ctx.docId
            });
            return program('program.docs');
        case 'upload_modification':
            return programId ? programDocs(programId, {
                tab: 'modifications'
            }) : '';
        case 'create_submission':
        case 'propose_submission_evidence':
        case 'propose_waiver_evidence':
        case 'propose_waiver_edit':
        case 'propose_submission_edit':
        case 'record_cor_answer':
        case 'log_contract_event':
            if (programId && ctx.occurrenceId) {
                return pathFor('program.deliverables.occurrence', {
                    programId,
                    occurrenceId: ctx.occurrenceId
                });
            }
            return program('program.deliverables');
        case 'create_work_product':
        case 'update_work_product':
            return program('program.teams');
        case 'extract_cpars_score':
        case 'upsert_cpars_score':
        case 'extract_qasp_score':
        case 'upsert_qasp_score':
            return program('program.quality');
        case 'upsert_deviation':
            return program('program.waivers-tailoring');
        case 'upsert_customer_touchpoint':
            return program('program.touchpoints');
        case 'upload_project_files':
            return ctx.projectId ? pathFor('project.documents', {
                projectId: ctx.projectId
            }) : '';
        case 'upload_award_intake':
            return ctx.preAwardProjectId ? pathFor('delivery.pre-award', {
                id: ctx.preAwardProjectId
            }) : '';
        case 'extract_pricing_sheet':
            return pathFor('registries');
        case 'file_company_document':
            return ctx.companyProfileId ? `${pathFor('registries.companies')}&record=${encodeURIComponent(ctx.companyProfileId)}` : pathFor('registries.companies');
        case 'link_capture_document':
            return ctx.captureProjectId ? pathFor('capture.documents', {
                id: ctx.captureProjectId
            }) : pathFor('capture');
        case 'attach_rfi_package':
            return ctx.rfiResponseProjectId ? pathFor('rfi-response', {
                id: ctx.rfiResponseProjectId
            }) : '';
        case 'save_external_capture_output':
            return ctx.captureProjectId ? pathFor('capture.outputs', {
                id: ctx.captureProjectId
            }) : pathFor('capture');
        case 'log_capture_interaction':
            return ctx.captureProjectId ? pathFor('capture.interactions', {
                id: ctx.captureProjectId
            }) : pathFor('capture');
        case 'log_rfi_event':
            return ctx.captureProjectId ? pathFor('capture.rfis', {
                id: ctx.captureProjectId
            }) : pathFor('capture');
        case 'record_capture_intel':
        case 'link_capture_competitor':
        case 'link_capture_teaming_partner':
        case 'upsert_capture_task':
        case 'update_capture_profile':
            return ctx.captureProjectId ? pathFor('capture.detail', {
                id: ctx.captureProjectId
            }) : pathFor('capture');
        default:
            return program('program.home');
    }
}
export function sowReceiptHref(programId) {
    return programDocs(programId, {
        tab: 'modifications'
    });
}
